import { ComponentType, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import styles from './TableWithExternalScroll.module.scss';

export const TableWithExternalScroll = <T extends object>(WrappedComponent: ComponentType<T>, biasSize?: number) => {
  return (props: T) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tableRef = useRef<any>(null);
    const [tableRefCurrent, setTableRefCurrent] = useState<HTMLTableElement>(tableRef.current);
    const externalScrollRef = useRef<HTMLDivElement>(null);
    const externalScrollRefCurrent = externalScrollRef.current;
    const [width, setWidth] = useState(0);
    const [scrollWidth, setScrollWidth] = useState(0);
    const isExternalScrollActive = scrollWidth > width;

    const calculateTableSize = useCallback(() => {
      if (tableRefCurrent !== null) {
        setWidth(tableRefCurrent.offsetWidth);
        setScrollWidth(tableRefCurrent.scrollWidth);
      }
    }, [tableRefCurrent]);

    useEffect(() => {
      setTableRefCurrent(tableRef.current);
    }, [externalScrollRefCurrent]);

    useEffect(() => {
      if (tableRefCurrent !== null) {
        calculateTableSize();
        tableRefCurrent.addEventListener('scroll', handleTableRefScroll);
        window.addEventListener('resize', calculateTableSize);
      }

      return () => {
        if (tableRefCurrent !== null) {
          tableRefCurrent.removeEventListener('scroll', handleTableRefScroll);
        }
        window.removeEventListener('resize', calculateTableSize);
      };
    }, [tableRefCurrent, calculateTableSize]);

    useEffect(() => {
      if (externalScrollRefCurrent !== null) {
        externalScrollRefCurrent.addEventListener('scroll', handleExternalRefScroll);
      }

      return () => {
        if (externalScrollRefCurrent !== null) {
          externalScrollRefCurrent.removeEventListener('scroll', handleExternalRefScroll);
        }
      };
    }, [externalScrollRefCurrent]);

    const handleTableRefScroll = () => {
      window.requestAnimationFrame(() => {
        if (tableRef.current !== null && externalScrollRef.current !== null) {
          externalScrollRef.current.scrollLeft = tableRef.current.scrollLeft;
        }
      });
    };

    const handleExternalRefScroll = () => {
      window.requestAnimationFrame(() => {
        if (tableRef.current !== null && externalScrollRef.current !== null) {
          tableRef.current.scrollLeft = externalScrollRef.current.scrollLeft;
        }
      });
    };

    const externalScrollBlockClassNames = cn(styles.ExternalScrollBlock, {
      [styles.ExternalScrollBlockActive]: isExternalScrollActive,
    });

    return (
      <div>
        <WrappedComponent innerRef={tableRef} recalculateExternalScroll={calculateTableSize} {...props} />
        <div className={externalScrollBlockClassNames}>
          <div
            className={styles.ExternalScroll}
            style={
              biasSize
                ? {
                    width: `calc(100% - ${biasSize}px)`,
                    marginLeft: biasSize,
                  }
                : undefined
            }
            ref={externalScrollRef}
          >
            <div className={styles.ExternalScrollBody} style={{ width: scrollWidth }} />
          </div>
        </div>
      </div>
    );
  };
};
